import { useMemo, useState } from "react";
import { MarketplaceListingCard } from "./MarketplaceListingCard";
import {
  marketplaceAvailabilityLabels,
  marketplaceCategoryLabels,
  type MarketplaceAvailability,
  type MarketplaceCategory,
  type MarketplaceListing,
} from "@/types/content";
import "./MarketplaceCatalogue.css";

const categories = Object.keys(marketplaceCategoryLabels) as MarketplaceCategory[];
const availabilities = Object.keys(marketplaceAvailabilityLabels) as MarketplaceAvailability[];

/**
 * The real, working browse/filter/search surface for the public catalogue.
 * Every filter here actually filters the real listing list — there is no
 * simulated search and no result that isn't in `marketplaceListings`.
 */
export function MarketplaceCatalogue({ listings }: { listings: MarketplaceListing[] }) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<MarketplaceCategory | "all">("all");
  const [availability, setAvailability] = useState<MarketplaceAvailability | "all">("all");

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return listings.filter((listing) => {
      if (category !== "all" && listing.category !== category) return false;
      if (availability !== "all" && listing.availability !== availability) return false;
      if (!normalizedQuery) return true;
      return (
        listing.name.toLowerCase().includes(normalizedQuery) ||
        listing.description.toLowerCase().includes(normalizedQuery) ||
        listing.publisher.toLowerCase().includes(normalizedQuery)
      );
    });
  }, [listings, query, category, availability]);

  return (
    <div className="marketplace-catalogue">
      <form
        className="marketplace-catalogue-controls"
        role="search"
        aria-label="Marketplace catalogue"
        onSubmit={(event) => event.preventDefault()}
      >
        <div className="marketplace-catalogue-field">
          <label htmlFor="marketplace-search">Search the catalogue</label>
          <input
            id="marketplace-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by name, publisher or description"
          />
        </div>
        <div className="marketplace-catalogue-field">
          <label htmlFor="marketplace-category">Category</label>
          <select
            id="marketplace-category"
            value={category}
            onChange={(event) => setCategory(event.target.value as MarketplaceCategory | "all")}
          >
            <option value="all">All categories</option>
            {categories.map((value) => (
              <option key={value} value={value}>
                {marketplaceCategoryLabels[value]}
              </option>
            ))}
          </select>
        </div>
        <div className="marketplace-catalogue-field">
          <label htmlFor="marketplace-availability">Availability</label>
          <select
            id="marketplace-availability"
            value={availability}
            onChange={(event) =>
              setAvailability(event.target.value as MarketplaceAvailability | "all")
            }
          >
            <option value="all">All availability</option>
            {availabilities.map((value) => (
              <option key={value} value={value}>
                {marketplaceAvailabilityLabels[value]}
              </option>
            ))}
          </select>
        </div>
      </form>

      <p className="marketplace-catalogue-count" role="status">
        {filtered.length} of {listings.length} catalogue{" "}
        {listings.length === 1 ? "entry" : "entries"}
      </p>

      {filtered.length > 0 ? (
        <div className="marketplace-catalogue-grid">
          {filtered.map((listing) => (
            <MarketplaceListingCard key={listing.id} listing={listing} />
          ))}
        </div>
      ) : (
        <div className="marketplace-catalogue-empty">
          <p>No catalogue entries match this search and filter combination.</p>
          <button
            type="button"
            className="marketplace-catalogue-reset"
            onClick={() => {
              setQuery("");
              setCategory("all");
              setAvailability("all");
            }}
          >
            Clear filters
          </button>
        </div>
      )}
    </div>
  );
}
